<script lang="ts">
    import { onDestroy, onMount } from "svelte"
    import Header from "../../../layout/global/header/Header.svelte"
    import Controls from "./Controls.svelte"
    import DefaultSlide from "./DefaultSlide.svelte"
    import { isHeroVisible } from "../../../../store"
    import SplittedSlide from "./SplittedSlide.svelte"
    const DEFAULT_SWIPE_TIMEOUT = 6000
    const MIN_SWIPE_TIMEOUT = 1000

    let {
        slides = [],
        splittedSlide,
        publication_date,
        autoSwipe = false,
        swipeTimeout,
    }: {
        slides?: Slide[]
        splittedSlide?: SplittedHeroRowBlock
        publication_date?: string
        autoSwipe?: boolean | string | number | null
        swipeTimeout?: number | string | null
    } = $props()
    let activeSlide = $state(0)
    let heroSliderWrapper: HTMLDivElement
    let autoSwipeInterval: ReturnType<typeof setInterval> | null = null

    function toBoolean(value: unknown): boolean {
        if (typeof value === "boolean") return value
        if (typeof value === "number") return value === 1
        if (typeof value === "string") {
            const normalized = value.trim().toLowerCase()
            return normalized === "true" || normalized === "1" || normalized === "yes"
        }
        return false
    }

    function parseTimeout(value: typeof swipeTimeout): number {
        if (typeof value === "number") {
            if (Number.isFinite(value) && value >= MIN_SWIPE_TIMEOUT) return value
            return DEFAULT_SWIPE_TIMEOUT
        }

        if (typeof value === "string" && value.trim() !== "") {
            const parsed = Number(value)
            if (Number.isFinite(parsed) && parsed >= MIN_SWIPE_TIMEOUT) return parsed
        }

        return DEFAULT_SWIPE_TIMEOUT
    }

    slides = slides
        .filter((slide): slide is Slide => Boolean(slide))
        .map((slide) => ({
            ...slide,
            inactive: toBoolean(slide?.inactive),
        }))
        .filter((slide) => !slide.inactive)

    const normalizedAutoSwipe = $derived(toBoolean(autoSwipe))
    const normalizedSwipeTimeout = $derived(parseTimeout(swipeTimeout))
    const autoSwipeEnabled = $derived(normalizedAutoSwipe && !splittedSlide && slides.length > 1)

    function goToNextSlide() {
        activeSlide = activeSlide + 1 >= slides.length ? 0 : activeSlide + 1
    }

    function goToPreviousSlide() {
        activeSlide = activeSlide - 1 < 0 ? slides.length - 1 : activeSlide - 1
    }

    function stopAutoSwipe() {
        if (autoSwipeInterval) {
            clearInterval(autoSwipeInterval)
            autoSwipeInterval = null
        }
    }

    function startAutoSwipe(interval = normalizedSwipeTimeout) {
        if (!autoSwipeEnabled || typeof window === "undefined") return
        stopAutoSwipe()
        autoSwipeInterval = window.setInterval(() => {
            goToNextSlide()
        }, interval)
    }

    $effect(() => {
        const currentInterval = normalizedSwipeTimeout
        stopAutoSwipe()
        if (autoSwipeEnabled) {
            startAutoSwipe(currentInterval)
        }
    })

    function handleNextClick() {
        goToNextSlide()
        startAutoSwipe()
    }

    function handlePrevClick() {
        goToPreviousSlide()
        startAutoSwipe()
    }

    if (typeof window !== "undefined") {
        onMount(() => {
            const targetElement = document.getElementById("hero-slider")
            if (!targetElement) {
                console.error("Element mit der ID 'xyz' wurde nicht gefunden.")
                return
            }

            const targetHeight = targetElement.offsetHeight

            const checkScroll = () => {
                const scrollY = window.scrollY || window.pageYOffset
                const visible = scrollY < targetHeight
                if ($isHeroVisible !== visible) $isHeroVisible = visible
            }

            checkScroll()
            window.addEventListener("scroll", checkScroll)
            return () => {
                window.removeEventListener("scroll", checkScroll)
            }
        })
    }

    onDestroy(() => {
        stopAutoSwipe()
    })
</script>

<div class="hero-slider-wrapper" bind:this={heroSliderWrapper}>
    {#if $isHeroVisible}
        <Header type="transparent" />
    {/if}
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <section class="hero-slider" id="hero-slider" onmouseenter={stopAutoSwipe} onmouseleave={() => startAutoSwipe()}>
        <ul>
            {#if splittedSlide}
                <li class="active">
                    <SplittedSlide slide={splittedSlide} />
                </li>
            {:else}
                {#each slides as slide, i}
                    <li class:active={i === activeSlide}>
                        <DefaultSlide {slide} {publication_date} />
                    </li>
                {/each}
            {/if}
        </ul>
        {#if slides.length > 1}
            <div class="control-row">
                <div>
                    <Controls
                        type="bright"
                        clickRight={() => {
                            handleNextClick()
                        }}
                        clickLeft={() => {
                            handlePrevClick()
                        }}
                    />
                </div>
            </div>
        {/if}
    </section>
</div>

<div class="placeholder-slider"></div>

<style lang="less">
    @import "../../../../css/variables.less";
    .placeholder-slider {
        height: 680px;
        background-color: transparent;
        position: relative;
        z-index: 1;
        width: 0px;
    }

    .hero-slider-wrapper {
        position: fixed;
        top: 0px;
        z-index: 1;
        height: 740px;
        width: 100%;
        .hero-slider {
            position: relative;
            height: 740px;
            ul {
                display: flex;
                position: relative;
                height: 100%;
                li {
                    position: absolute;
                    opacity: 0;
                    transition: opacity 0.3s;
                    z-index: 0;
                    left: 0px;
                    top: 0px;
                    height: 100%;
                    width: 100%;
                    &.active {
                        opacity: 1;
                        z-index: 1;
                    }
                }
            }
            .control-row {
                position: absolute;
                width: 100%;
                display: flex;
                justify-content: center;
                bottom: calc(var(--default-padding) * 1.5);
                & > div {
                    width: 100%;
                    max-width: var(--extended-max-width);
                    margin: 0px var(--default-padding);
                    display: flex;
                    justify-content: flex-end;
                }
                @media @max-mobile {
                    bottom: calc(var(--default-padding) * 5);
                }
            }
        }
    }
</style>
